/**
 * Life4Earth V2 — Admin per-user access control API adapter.
 *
 * Backs the Admin Console "what can this user see / do" modal: read a user's
 * archetype + completed milestones + manual overrides, and write overrides or
 * set the archetype. Effective features are computed client-side from
 * archetypeConfig.ts (baseline + milestones) with these overrides applied.
 *
 * All endpoints require system admin (enforced server-side by [RequireSystemAdmin]).
 */

import { apiFetch } from './client';

export interface AdminUserFeatureState {
  userGuid: string;
  hasProfile: boolean;
  archetypeSlug: string | null;
  originalArchetypeSlug: string | null;
  onboardingComplete: boolean;
  completedMilestones: string[];
  /** Manual overrides: {feature_key: true/false}. */
  featureOverrides: Record<string, boolean>;
}

/** GET the user's archetype + milestones + overrides. */
export async function fetchUserFeatureState(userGuid: string): Promise<AdminUserFeatureState> {
  return apiFetch<AdminUserFeatureState>(`/api/admin/users/${userGuid}/features`);
}

/** PUT the full manual-override map (force features on/off). Returns fresh state. */
export async function setUserFeatureOverrides(
  userGuid: string,
  overrides: Record<string, boolean>,
): Promise<AdminUserFeatureState> {
  return apiFetch<AdminUserFeatureState>(`/api/admin/users/${userGuid}/feature-overrides`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ overrides }),
  });
}

/** PUT a manual archetype assignment (override the progression). Returns fresh state. */
export async function setUserArchetype(
  userGuid: string,
  archetypeSlug: string,
): Promise<AdminUserFeatureState> {
  return apiFetch<AdminUserFeatureState>(`/api/admin/users/${userGuid}/archetype`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ archetypeSlug }),
  });
}
